import React, {useMemo} from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';

import ScreenDimensions from '../../../../constants/ScreenDimensionsConfig';
import {PAYMENT_SERVICE_TYPES, usePaymentServices} from '../../../../queries/paymentCard';
import theme from '../../../../style/theme';
import Text from '../../../themed/Text';

import {useApplePayAvailability} from './applePay';
import {ApplePayIcon, CardBrandIcon, GooglePayIcon, SupportedCardIcons} from './PaymentIcons';
import type {SavedCard} from './types';

type AccountPaymentMethodCardProps = {
  onAddCard: () => void;
  onChangeCard: () => void;
  onDeleteCard: () => void;
  isCardActionLoading?: boolean;
};

function formatExpiry(month: number, year: number): string {
  const mm = month.toString().padStart(2, '0');
  const yy = year.toString().slice(-2);
  return `${mm}/${yy}`;
}

export default function AccountPaymentMethodCard({
  onAddCard,
  onChangeCard,
  onDeleteCard,
  isCardActionLoading = false,
}: AccountPaymentMethodCardProps) {
  const {width: viewportWidth} = useWindowDimensions();
  const isWebDesktop =
    Platform.OS === 'web' && viewportWidth > ScreenDimensions.mobileOrTablet.maxWidth;

  // Fetch payment services from server
  const {data: paymentServicesData, isLoading, error} = usePaymentServices();

  // Check Apple Pay device availability (web only, native handled by Platform.OS)
  const {canMakePayments: applePayDeviceAvailable} = useApplePayAvailability({
    skip: Platform.OS !== 'web',
  });

  // Derive saved card from till_payments service
  const savedCard = useMemo((): SavedCard | undefined => {
    const tillService = paymentServicesData?.services.find(
      s => s.type === PAYMENT_SERVICE_TYPES.TILL_PAYMENTS,
    );
    const card = tillService?.saved_card;
    if (!card) return undefined;

    return {
      cardType: card.card_type,
      lastFourDigits: card.obfuscated_card?.slice(-4) ?? '',
      expiryMonth: card.expiry_month,
      expiryYear: card.expiry_year,
    };
  }, [paymentServicesData?.services]);

  // Check server availability for Apple Pay and Google Pay
  const serverHasApplePay = useMemo(() => {
    return paymentServicesData?.services.some(s => s.type === 'apple_pay') ?? false;
  }, [paymentServicesData?.services]);

  const serverHasGooglePay = useMemo(() => {
    return paymentServicesData?.services.some(s => s.type === 'google_pay') ?? false;
  }, [paymentServicesData?.services]);

  // Determine if we should show Apple Pay badge
  // Server must have it enabled AND device must support it
  const showApplePay = useMemo(() => {
    if (!serverHasApplePay) return false;
    if (Platform.OS === 'ios') return true; // Native iOS always supports
    if (Platform.OS === 'web') return applePayDeviceAvailable;
    return false; // Android doesn't support Apple Pay
  }, [serverHasApplePay, applePayDeviceAvailable]);

  // Determine if we should show Google Pay badge
  // Server must have it enabled AND platform must support it
  const showGooglePay = useMemo(() => {
    if (!serverHasGooglePay) return false;
    if (Platform.OS === 'android') return true;
    if (Platform.OS === 'web') return true; // Google Pay works on most web browsers
    return false; // iOS doesn't support Google Pay
  }, [serverHasGooglePay]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>Payment Method</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colour.uiControls?.primary ?? '#0054A6'} />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>Payment Method</Text>
        </View>
        <View style={styles.cardContainer}>
          <View style={styles.errorContent}>
            <Text style={styles.errorText}>Unable to load payment information</Text>
            <Text style={styles.errorSubtext}>Please try again later</Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header with title and wallet badges */}
      <View style={styles.headerRow}>
        <Text style={styles.title}>Payment Method</Text>
        <View style={styles.badgesRow}>
          {showApplePay ? <ApplePayIcon size="small" /> : null}
          {showGooglePay ? <GooglePayIcon size="small" /> : null}
        </View>
      </View>

      {/* Card container - PaymentMethodOption style */}
      <View style={styles.cardContainer}>
        {savedCard ? (
          /* Card exists - show card details */
          <View style={styles.cardContent}>
            <View style={styles.cardRow}>
              <View style={styles.cardInfo}>
                <Text style={styles.cardLabel}>
                  {savedCard.cardType.toUpperCase()} ****{savedCard.lastFourDigits}
                </Text>
                <Text style={styles.cardExpiry}>
                  Expiry {formatExpiry(savedCard.expiryMonth, savedCard.expiryYear)}
                </Text>
              </View>
              <View style={styles.iconWrapper}>
                <CardBrandIcon cardType={savedCard.cardType} />
              </View>
            </View>
            <View style={styles.actionsRow}>
              {isCardActionLoading ? (
                <ActivityIndicator
                  size="small"
                  color={theme.colour.uiControls?.primary ?? '#0054A6'}
                />
              ) : (
                <>
                  <Pressable onPress={onChangeCard} style={styles.actionButton}>
                    <Text style={styles.actionText}>CHANGE CARD</Text>
                    <Text style={styles.actionArrow}> →</Text>
                  </Pressable>
                  <Pressable onPress={onDeleteCard} style={styles.actionButton}>
                    <Text style={[styles.actionText, styles.deleteText]}>DELETE</Text>
                    <Text style={[styles.actionArrow, styles.deleteText]}> →</Text>
                  </Pressable>
                </>
              )}
            </View>
          </View>
        ) : (
          /* No card - two-line layout on mobile (web + native): label + "Add
             Card →" on row 1, SupportedCardIcons on row 2. Desktop inlines
             the icons next to the label. Mirrors PaymentMethodOption's
             showAddCardAction branch, minus the radio button. */
          <Pressable
            style={styles.addCardPressable}
            onPress={onAddCard}
            testID="account-payment-method-card-add-pressable"
            accessibilityRole="button"
            accessibilityLabel="Credit/Debit Card">
            <View testID="account-payment-method-card-add-top-row" style={styles.cardAddTopRow}>
              <View
                style={[styles.addCardLabelContainer, isWebDesktop && styles.cardAddDesktopRow]}>
                <Text style={styles.addCardLabel} numberOfLines={1}>
                  Credit/Debit Card
                </Text>
                {isWebDesktop ? (
                  <View style={styles.cardAddInlineIcons}>
                    <SupportedCardIcons size="small" />
                  </View>
                ) : null}
              </View>
              <View style={styles.addActionInline}>
                <Text style={styles.actionText}>Add Card</Text>
                <Text style={styles.actionArrow}> →</Text>
              </View>
            </View>
            {!isWebDesktop ? (
              <View
                testID="account-payment-method-card-supported-icons-row"
                style={styles.cardAddIconsRow}>
                <SupportedCardIcons size="small" />
              </View>
            ) : null}
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  loadingContainer: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    backgroundColor: '#FFF',
    padding: 40,
    alignItems: 'center',
  },
  cardContainer: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    backgroundColor: '#FFF',
    overflow: 'hidden',
  },
  errorContent: {
    padding: 24,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#DC2626',
    marginBottom: 4,
  },
  errorSubtext: {
    fontSize: 13,
    color: '#666',
  },
  cardContent: {
    padding: 16,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardInfo: {
    flex: 1,
  },
  cardLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  cardExpiry: {
    fontSize: 14,
    color: '#666',
  },
  iconWrapper: {
    marginLeft: 12,
  },
  actionsRow: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 24,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colour.uiControls?.primary ?? '#0054A6',
  },
  actionArrow: {
    fontSize: 14,
    color: theme.colour.uiControls?.primary ?? '#0054A6',
  },
  deleteText: {
    color: '#C81E1E',
  },
  addCardPressable: {
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  cardAddTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addCardLabelContainer: {
    flex: 1,
  },
  cardAddDesktopRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addCardLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  cardAddInlineIcons: {
    marginLeft: 8,
  },
  cardAddIconsRow: {
    marginTop: 8,
    alignItems: 'flex-start',
  },
  addActionInline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
    paddingLeft: 12,
  },
});
